/**
 * Getting "hide me" to the server, however long it takes.
 *
 * Split out of `session-store.ts` for the same reason `presence-heartbeat.ts`
 * is: the store imports `react-native`, `@/api` and `@/i18n`, so nothing in it
 * can be driven by a real test. This module imports nothing and takes its
 * dependencies as arguments.
 *
 * ## Why a hide gets retried when a publish does not
 *
 * Becoming visible already has a retry: the presence heartbeat runs while
 * visible and re-checks-in and re-publishes every beat, so a failed publish
 * fixes itself. Going hidden stops that heartbeat, so a failed hide has nothing
 * behind it — and it is the direction where being wrong actually costs the
 * user something. The switch says hidden the instant they tap it; this is what
 * keeps that from being a lie.
 *
 * ## What bounds the lie
 *
 * Not this loop, which is deliberately short — a phone in a tunnel is not
 * helped by a sixth attempt. With the heartbeat stopped, nothing refreshes
 * `last_seen`, and the server drops anyone unseen for `presenceTtl` (300s). An
 * unreachable hide becomes a real one within five minutes even if every attempt
 * here fails and the app is never opened again. The loop makes it immediate;
 * the TTL makes it certain.
 */

/**
 * Delays before each attempt, in milliseconds. The first is zero: the common
 * case is that the very first call works, and paying a second for it would make
 * every ordinary hide feel broken.
 */
export const HIDE_RETRY_DELAYS_MS = [0, 1000, 3000, 8000, 20000] as const;

export type HideRetryDeps = {
  /** Ask the server to hide this person. Rejects on any failure. */
  attempt: () => Promise<void>;
  /**
   * Does this hide still represent what the user wants?
   *
   * Checked before every wait and again after it. A user who taps hidden and
   * then visible again while attempt three is sleeping must NOT be hidden by
   * attempt four — a late write from a superseded intent is how an optimistic
   * switch turns into a switch that flips itself back.
   */
  isCurrent: () => boolean;
  sleep: (ms: number) => Promise<void>;
};

export type HideRetryOutcome =
  /** The server has been told. */
  | "settled"
  /** A newer intent took over; this one no longer owns the outcome. */
  | "superseded"
  /** Every attempt failed. The caller re-arms this on foreground. */
  | "unsettled";

/**
 * Try until the server accepts the hide, the intent is superseded, or the
 * attempts run out.
 *
 * Never throws. There is nothing the user could do with the news, and they have
 * already been shown what they asked for.
 */
export async function runHideWithRetry(
  deps: HideRetryDeps,
  delaysMs: readonly number[] = HIDE_RETRY_DELAYS_MS,
): Promise<HideRetryOutcome> {
  for (const wait of delaysMs) {
    if (!deps.isCurrent()) return "superseded";
    if (wait) await deps.sleep(wait);
    if (!deps.isCurrent()) return "superseded";

    try {
      await deps.attempt();
      return "settled";
    } catch {
      // Keep whatever attempts remain.
    }
  }
  return "unsettled";
}
